import React, { useState, useEffect } from "react";
import FlowHeader from "../../../components/FlowHeader";
import { useNavigate } from "react-router-dom";
import { usePurchase } from "../../../context/PurchaseContext";
import { useCart } from "../../../hooks/useCart";
import { locationService } from "../../../services/location";
import { authService } from "../../../services/auth";
import { orderService } from "../../../services/orderService";

const StepProgress = () => {
  return (
    <div className="w-[427px]">
      <div className="flex items-center">
        <div className="flex items-center relative flex-1">
          {/* İlk Nokta */}
          <div className="flex flex-col items-center">
            <div className="w-4 h-4 bg-[#4F46E5] rounded-full z-10"></div>
            <span className="text-xs mt-2 text-[#4F46E5] font-medium absolute -bottom-6 whitespace-nowrap">
              Halı Detayları
            </span>
          </div>
          {/* İlk Çizgi */}
          <div className="flex-1 h-[2px] bg-[#4F46E5] ml-0"></div>
        </div>

        <div className="flex items-center relative flex-1">
          {/* İkinci Nokta */}
          <div className="flex flex-col items-center">
            <div className="w-4 h-4 bg-[#4F46E5] rounded-full z-10"></div>
            <span className="text-xs mt-2 text-[#4F46E5] font-medium absolute -bottom-6 whitespace-nowrap">
              Adres Bilgileri
            </span>
          </div>
          {/* İkinci Çizgi */}
          <div className="flex-1 h-[2px] bg-gray-200"></div>
        </div>

        <div className="flex items-center relative">
          {/* Son Nokta */}
          <div className="flex flex-col items-center">
            <div className="w-4 h-4 border-2 border-gray-200 rounded-full bg-white z-10"></div>
            <span className="text-xs mt-2 text-gray-400 font-medium absolute -bottom-6 whitespace-nowrap">
              Ödeme
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const AddressDetails = () => {
  const navigate = useNavigate();
  const { purchaseData, updateContact, updateAddress, updateCreateAccount } = usePurchase();
  const { cartItems } = useCart();
  
  // Form state
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [semts, setSemts] = useState([]);
  const [neighbourhoods, setNeighbourhoods] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedSemt, setSelectedSemt] = useState("");
  const [selectedNeighbourhood, setSelectedNeighbourhood] = useState("");
  const [addressLine, setAddressLine] = useState("");
  const [createAccount, setCreateAccount] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Sepetteki ilk ürünün bayi bilgilerini al
  const firstCartItem = cartItems[0];
  const dealerName = firstCartItem?.dealer_name || "Bayi";
  
  // Tahmini teslimat tarihini al (PurchaseContext'ten)
  const getFormattedDeliveryDate = () => {
    if (!purchaseData.estimatedDeliveryDate) return "";
    
    const date = new Date(purchaseData.estimatedDeliveryDate);
    const days = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
    const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
    
    const dayName = days[date.getDay()];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    return `${day} ${month} ${year} - ${dayName}`;
  };
  
  // Mevcut kullanıcı bilgilerini yükle
  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    const isGuest = !currentUser || !currentUser.id;
    
    if (currentUser) {
      setName(currentUser.name || "");
      setPhone(currentUser.phone || "");
    }
    
    // Context'ten mevcut bilgileri yükle - null kontrolü ile
    if (purchaseData?.contact?.phone) setPhone(purchaseData.contact.phone);
    if (purchaseData?.contact?.name) setName(purchaseData.contact.name);
    if (purchaseData?.address?.provinceId) setSelectedProvince(purchaseData.address.provinceId);
    if (purchaseData?.address?.districtId) setSelectedDistrict(purchaseData.address.districtId);
    if (purchaseData?.address?.neighbourhoodId) setSelectedNeighbourhood(purchaseData.address.neighbourhoodId);
    if (purchaseData?.address?.line1) setAddressLine(purchaseData.address.line1);
    
    // Misafir kullanıcı ise üyelik oluştur seçeneğini göster
    if (isGuest && purchaseData?.createAccount === undefined) {
      setCreateAccount(false);
    } else if (purchaseData?.createAccount !== undefined) {
      setCreateAccount(purchaseData.createAccount);
    }
    
    // İlleri yükle
    loadProvinces();
  }, [purchaseData]);
  
  // İl değiştiğinde ilçeleri yükle
  useEffect(() => {
    if (selectedProvince) {
      loadDistricts(selectedProvince);
    }
  }, [selectedProvince]);
  
  // İlçe değiştiğinde semtleri yükle
  useEffect(() => {
    if (selectedDistrict) {
      loadSemts(selectedDistrict);
    } else {
      setSemts([]);
      setSelectedSemt("");
      setNeighbourhoods([]);
      setSelectedNeighbourhood("");
    }
  }, [selectedDistrict]);
  
  // Semt değiştiğinde mahalleleri yükle
  useEffect(() => {
    if (selectedSemt) {
      loadNeighbourhoods(selectedSemt);
    } else {
      setNeighbourhoods([]);
      setSelectedNeighbourhood("");
    }
  }, [selectedSemt]);
  
  const loadProvinces = async () => {
    try {
      setLoading(true);
      console.log("AddressDetails: İller yükleniyor...");
      const response = await locationService.getCities();
      console.log("AddressDetails: Gelen yanıt:", response);
      
      // API yanıtını kontrol et - [null, Array] formatını handle et
      let cities = [];
      if (Array.isArray(response)) {
        // Eğer response [null, Array] formatındaysa
        if (response.length === 2 && response[0] === null && Array.isArray(response[1])) {
          cities = response[1];
        } else {
          // Normal array ise direkt kullan
          cities = response;
        }
      } else if (response && Array.isArray(response.data)) {
        cities = response.data;
      } else if (response && response.status === 1 && Array.isArray(response.data)) {
        cities = response.data;
      }
      
      // Eğer cities hala [null, Array] formatındaysa tekrar kontrol et
      if (Array.isArray(cities) && cities.length === 2 && cities[0] === null && Array.isArray(cities[1])) {
        cities = cities[1];
      }
      
      console.log("AddressDetails: İşlenecek şehirler:", cities);
      
      // Null ve undefined değerleri filtrele - API'den id/name geliyor, city_id/city_name değil
      const validCities = cities.filter(city => city && (city.city_id || city.id));
      console.log("AddressDetails: Filtrelenmiş şehirler:", validCities);
      setProvinces(validCities);
    } catch (error) {
      console.error("İller yüklenemedi:", error);
      setProvinces([]);
    } finally {
      setLoading(false);
    }
  };
  
  const loadDistricts = async (cityId) => {
    try {
      setLoading(true);
      console.log("AddressDetails: İlçeler yükleniyor, cityId:", cityId);
      const response = await locationService.getDistricts(cityId);
      console.log("AddressDetails: Gelen ilçe yanıtı:", response);
      
      // API yanıtını kontrol et - [null, Array] formatını handle et
      let districts = [];
      if (Array.isArray(response)) {
        // Eğer response [null, Array] formatındaysa
        if (response.length === 2 && response[0] === null && Array.isArray(response[1])) {
          districts = response[1];
        } else {
          // Normal array ise direkt kullan
          districts = response;
        }
      } else if (response && Array.isArray(response.data)) {
        districts = response.data;
      } else if (response && response.status === 1 && Array.isArray(response.data)) {
        districts = response.data;
      }
      
      // Eğer districts hala [null, Array] formatındaysa tekrar kontrol et
      if (Array.isArray(districts) && districts.length === 2 && districts[0] === null && Array.isArray(districts[1])) {
        districts = districts[1];
      }
      
      console.log("AddressDetails: İşlenecek ilçeler:", districts);
      
      // Null ve undefined değerleri filtrele - API'den id/name geliyor, district_id/district_name değil
      const validDistricts = districts.filter(district => district && (district.district_id || district.id));
      console.log("AddressDetails: Filtrelenmiş ilçeler:", validDistricts);
      setDistricts(validDistricts);
      setSelectedDistrict(""); // İl değişince ilçeyi sıfırla
      setSemts([]); // Semtleri sıfırla
      setSelectedSemt(""); // Semt seçimini sıfırla
      setNeighbourhoods([]); // Mahalleleri sıfırla
      setSelectedNeighbourhood(""); // Mahalle seçimini sıfırla
    } catch (error) {
      console.error("İlçeler yüklenemedi:", error);
      setDistricts([]);
    } finally {
      setLoading(false);
    }
  };
  
  const loadSemts = async (districtId) => {
    try {
      setLoading(true);
      console.log("AddressDetails: Semtler yükleniyor, districtId:", districtId);
      const response = await locationService.getSemts(districtId);
      console.log("AddressDetails: Gelen semt yanıtı:", response);
      
      // API yanıtını kontrol et
      let semts = [];
      if (Array.isArray(response)) {
        if (response.length === 2 && response[0] === null && Array.isArray(response[1])) {
          semts = response[1];
        } else {
          semts = response;
        }
      } else if (response && Array.isArray(response.data)) {
        semts = response.data;
      }
      
      console.log("AddressDetails: İşlenecek semtler:", semts);
      
      const validSemts = semts.filter(semt => semt && (semt.semt_id || semt.id));
      console.log("AddressDetails: Filtrelenmiş semtler:", validSemts);
      setSemts(validSemts);
      setSelectedSemt("");
      setNeighbourhoods([]);
      setSelectedNeighbourhood("");
    } catch (error) {
      console.error("Semtler yüklenemedi:", error);
      setSemts([]);
    } finally {
      setLoading(false);
    }
  };
  
  const loadNeighbourhoods = async (semtId) => {
    try {
      setLoading(true);
      console.log("AddressDetails: Mahalleler yükleniyor, semtId:", semtId);
      const response = await locationService.getNeighbourhoods(semtId);
      console.log("AddressDetails: Gelen mahalle yanıtı:", response);
      
      // API yanıtını kontrol et
      let neighbourhoods = [];
      if (Array.isArray(response)) {
        if (response.length === 2 && response[0] === null && Array.isArray(response[1])) {
          neighbourhoods = response[1];
        } else {
          neighbourhoods = response;
        }
      } else if (response && Array.isArray(response.data)) {
        neighbourhoods = response.data;
      }
      
      console.log("AddressDetails: İşlenecek mahalleler:", neighbourhoods);
      
      const validNeighbourhoods = neighbourhoods.filter(n => n && (n.mahalle_id || n.id));
      console.log("AddressDetails: Filtrelenmiş mahalleler:", validNeighbourhoods);
      setNeighbourhoods(validNeighbourhoods);
      setSelectedNeighbourhood("");
    } catch (error) {
      console.error("Mahalleler yüklenemedi:", error);
      setNeighbourhoods([]);
    } finally {
      setLoading(false);
    }
  };
  
  const formatPhoneNumber = (value) => {
    // Sadece rakamları al
    const numbers = value.replace(/\D/g, '');
    
    // 10 haneden fazla girmeye izin verme
    if (numbers.length > 10) return phone;
    
    // Formatla: (5XX) XXX XX XX
    let formatted = '';
    if (numbers.length > 0) {
      formatted = '(' + numbers.substring(0, 3);
      if (numbers.length > 3) {
        formatted += ') ' + numbers.substring(3, 6);
      }
      if (numbers.length > 6) {
        formatted += ' ' + numbers.substring(6, 8);
      }
      if (numbers.length > 8) {
        formatted += ' ' + numbers.substring(8, 10);
      }
    }
    
    return formatted;
  };
  
  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
    
    // Hata varsa temizle
    if (errors.phone) {
      setErrors(prev => ({ ...prev, phone: undefined }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Telefon kontrolü
    const phoneNumbers = phone.replace(/\D/g, '');
    if (!phoneNumbers || phoneNumbers.length !== 10) {
      newErrors.phone = "Geçerli bir telefon numarası giriniz.";
    }
    
    // Ad kontrolü
    if (!name.trim()) {
      newErrors.name = "Ad soyad giriniz.";
    }
    
    // İl kontrolü
    if (!selectedProvince) {
      newErrors.province = "İl seçiniz.";
    }
    
    // İlçe kontrolü
    if (!selectedDistrict) {
      newErrors.district = "İlçe seçiniz.";
    }
    
    // Semt kontrolü
    if (!selectedSemt) {
      newErrors.semt = "Semt seçiniz.";
    }
    
    // Mahalle kontrolü
    if (!selectedNeighbourhood) {
      newErrors.neighbourhood = "Mahalle seçiniz.";
    }
    
    // Açık adres kontrolü
    if (!addressLine.trim()) {
      newErrors.addressLine = "Açık adres giriniz.";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleNext = async () => {
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const currentUser = authService.getCurrentUser();
      const customerId = currentUser?.id;
      
      // Kategori bazlı field ismi belirle
      const categoryId = firstCartItem?.category_id || "1";
      let itemTypeField = "carpetTypes"; // Varsayılan
      
      switch (categoryId) {
        case "1":
          itemTypeField = "carpetTypes";
          break;
        case "2":
          itemTypeField = "furnitureTypes";
          break;
        case "3":
          itemTypeField = "curtainTypes";
          break;
        case "4":
          itemTypeField = "duvetTypes";
          break;
        case "5":
          itemTypeField = "houseCleaningTypes";
          break;
        default:
          itemTypeField = "itemTypes";
      }
      
      // Sepetteki tüm bayi ID'lerini al
      const dealerIds = [...new Set(cartItems.map(item => item.dealer_id))];
      console.log("Sepetteki bayiler:", dealerIds);
      
      // Sipariş verilerini hazırla
      const orderData = {
        [itemTypeField]: purchaseData.carpetTypes || [], // Dinamik field ismi
        contact: {
          phone: phone.replace(/\D/g, ''), // Sadece rakamlar
          name: name.trim()
        },
        address: {
          provinceId: selectedProvince,
          districtId: selectedDistrict,
          semtId: selectedSemt,
          neighbourhoodId: selectedNeighbourhood,
          line1: addressLine.trim()
        },
        createAccount: createAccount,
        estimatedDeliveryDate: purchaseData.estimatedDeliveryDate || new Date().toISOString(),
        dealerId: purchaseData.dealerId || firstCartItem?.dealer_id, // İlk bayinin ID'si
        dealerIds: dealerIds, // Tüm bayi ID'leri (API destekliyorsa)
        productDetails: purchaseData.productDetails || {} // Ürün detayları
      };
      
      // Eğer kullanıcı giriş yapmışsa customerId ekle
      if (customerId) {
        orderData.customerId = customerId;
      }
      
      console.log("Sipariş oluşturuluyor:", orderData);
      
      // Siparişi oluştur
      const result = await orderService.createNewOnePageOrder(orderData);
      
      console.log("Sipariş oluşturuldu:", result);
      
      // Form bilgilerini context'e kaydet
      updateContact({
        phone: phone.replace(/\D/g, ''),
        name: name.trim()
      });
      
      updateAddress({
        provinceId: selectedProvince,
        districtId: selectedDistrict,
        neighbourhoodId: selectedNeighbourhood || "0",
        line1: addressLine.trim()
      });
      
      updateCreateAccount(createAccount);
      
      // Sipariş ID'sini context'e kaydet (Payment sayfası için)
      if (result.orderId || result.order_id) {
        // PurchaseContext'e orderId eklenmeli - şimdilik navigate state ile gönder
        navigate("/purchase/carpet-cleaning/payment", {
          state: {
            orderId: result.orderId || result.order_id,
            orderResponse: result
          }
        });
      } else {
        throw new Error("Sipariş ID alınamadı.");
      }
      
    } catch (error) {
      console.error("Sipariş oluşturma hatası:", error);
      alert(error.message || "Sipariş oluşturulurken bir hata oluştu.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <FlowHeader />

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="flex justify-center">
          <div className="w-[427px]">
            <StepProgress />

            {/* Info Boxes */}
            <div className="mt-[58px] space-y-2.5 mb-8">
              <div className="inline-flex items-center border border-black rounded-full py-2 px-4">
                <span className="text-sm">
                  <span className="font-medium">Satıcı / Sağlayıcı : </span>
                  {dealerName}
                </span>
              </div>
              <div className="block">
                <div className="inline-flex items-center border border-black rounded-full py-2 px-4">
                  <span className="text-sm">
                    <span className="font-medium">Tahmini Teslim : </span>
                    {getFormattedDeliveryDate()}
                  </span>
                </div>
              </div>
            </div>

            {/* Ayırıcı Çizgi */}
            <div className="mb-8">
              <div className="w-[424px] h-[0.5px] bg-black opacity-50"></div>
            </div>

            {/* İletişim ve Adres Bilgileri Formu */}
            <div>
              <h2 className="text-xl font-semibold mb-6">İletişim Bilgileri</h2>
              <div className="space-y-4">
                {/* Telefon */}
                <div className="relative">
                  <input
                    type="tel"
                    value={phone}
                    onChange={handlePhoneChange}
                    placeholder="(5XX) XXX XX XX"
                    className={`w-full p-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent ${
                      errors.phone ? 'border-red-300' : 'border-gray-200'
                    }`}
                  />
                  {errors.phone && (
                    <p className="text-sm text-red-600 mt-1">{errors.phone}</p>
                  )}
                </div>
                
                {/* Ad Soyad */}
                <div className="relative">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (errors.name) {
                        setErrors(prev => ({ ...prev, name: undefined }));
                      }
                    }}
                    placeholder="Ad Soyad"
                    className={`w-full p-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent ${
                      errors.name ? 'border-red-300' : 'border-gray-200'
                    }`}
                  />
                  {errors.name && (
                    <p className="text-sm text-red-600 mt-1">{errors.name}</p>
                  )}
                </div>

                {/* İl */}
                <div className="relative">
                  <label className="block text-sm mb-1">İl</label>
                  <div className="relative">
                    <select 
                      value={selectedProvince}
                      onChange={(e) => {
                        setSelectedProvince(e.target.value);
                        if (errors.province) {
                          setErrors(prev => ({ ...prev, province: undefined }));
                        }
                      }}
                      disabled={loading}
                      className={`w-full p-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent appearance-none bg-white pr-10 ${
                        errors.province ? 'border-red-300' : 'border-gray-200'
                      } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <option value="">İl Seçiniz</option>
                      {provinces.filter(province => province && (province.city_id || province.id)).map(province => (
                        <option key={province.city_id || province.id} value={province.city_id || province.id}>
                          {province.city_name || province.name || 'İsimsiz'}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none">
                      <svg
                        className="w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                  {errors.province && (
                    <p className="text-sm text-red-600 mt-1">{errors.province}</p>
                  )}
                </div>

                {/* İlçe */}
                <div className="relative">
                  <label className="block text-sm mb-1">İlçe</label>
                  <div className="relative">
                    <select 
                      value={selectedDistrict}
                      onChange={(e) => {
                        setSelectedDistrict(e.target.value);
                        if (errors.district) {
                          setErrors(prev => ({ ...prev, district: undefined }));
                        }
                      }}
                      disabled={!selectedProvince || loading}
                      className={`w-full p-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent appearance-none bg-white pr-10 ${
                        errors.district ? 'border-red-300' : 'border-gray-200'
                      } ${!selectedProvince || loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <option value="">İlçe Seçiniz</option>
                      {districts.filter(district => district && (district.district_id || district.id)).map(district => (
                        <option key={district.district_id || district.id} value={district.district_id || district.id}>
                          {district.district_name || district.name || 'İsimsiz'}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none">
                      <svg
                        className="w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                  {errors.district && (
                    <p className="text-sm text-red-600 mt-1">{errors.district}</p>
                  )}
                </div>

                {/* Semt */}
                <div className="relative">
                  <label className="block text-sm mb-1">Semt</label>
                  <div className="relative">
                    <select 
                      value={selectedSemt}
                      onChange={(e) => {
                        setSelectedSemt(e.target.value);
                        if (errors.semt) {
                          setErrors(prev => ({ ...prev, semt: undefined }));
                        }
                      }}
                      disabled={!selectedDistrict || loading}
                      className={`w-full p-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent appearance-none bg-white pr-10 ${
                        errors.semt ? 'border-red-300' : 'border-gray-200'
                      } ${!selectedDistrict || loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <option value="">Semt Seçiniz</option>
                      {semts.filter(semt => semt && (semt.semt_id || semt.id)).map(semt => (
                        <option key={semt.semt_id || semt.id} value={semt.semt_id || semt.id}>
                          {semt.semt_name || semt.name || 'İsimsiz'}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none">
                      <svg
                        className="w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                  {errors.semt && (
                    <p className="text-sm text-red-600 mt-1">{errors.semt}</p>
                  )}
                </div>

                {/* Mahalle */}
                <div className="relative">
                  <label className="block text-sm mb-1">Mahalle</label>
                  <div className="relative">
                    <select 
                      value={selectedNeighbourhood}
                      onChange={(e) => {
                        setSelectedNeighbourhood(e.target.value);
                        if (errors.neighbourhood) {
                          setErrors(prev => ({ ...prev, neighbourhood: undefined }));
                        }
                      }}
                      disabled={!selectedSemt || loading}
                      className={`w-full p-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent appearance-none bg-white pr-10 ${
                        errors.neighbourhood ? 'border-red-300' : 'border-gray-200'
                      } ${!selectedSemt || loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <option value="">Mahalle Seçiniz</option>
                      {neighbourhoods.filter(n => n && (n.mahalle_id || n.id)).map(neighbourhood => (
                        <option key={neighbourhood.mahalle_id || neighbourhood.id} value={neighbourhood.mahalle_id || neighbourhood.id}>
                          {neighbourhood.mahalle_name || neighbourhood.name || 'İsimsiz'}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none">
                      <svg
                        className="w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                  {errors.neighbourhood && (
                    <p className="text-sm text-red-600 mt-1">{errors.neighbourhood}</p>
                  )}
                </div>

                {/* Açık Adres */}
                <div className="relative">
                  <label className="block text-sm mb-1">Açık Adres</label>
                  <textarea
                    value={addressLine}
                    onChange={(e) => {
                      setAddressLine(e.target.value);
                      if (errors.addressLine) {
                        setErrors(prev => ({ ...prev, addressLine: undefined }));
                      }
                    }}
                    placeholder="Açık Adres Yazınız"
                    rows={4}
                    className={`w-full p-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent resize-none ${
                      errors.addressLine ? 'border-red-300' : 'border-gray-200'
                    }`}
                  />
                  {errors.addressLine && (
                    <p className="text-sm text-red-600 mt-1">{errors.addressLine}</p>
                  )}
                </div>

                {/* Üyelik Checkbox - Sadece misafir kullanıcılar için göster */}
                {(!authService.getCurrentUser() || !authService.getCurrentUser()?.id) && (
                  <div className="flex items-center mt-4">
                    <input
                      type="checkbox"
                      id="createAccount"
                      checked={createAccount}
                      onChange={(e) => setCreateAccount(e.target.checked)}
                      className="w-4 h-4 text-[#4F46E5] border-gray-300 rounded focus:ring-[#4F46E5]"
                    />
                    <label
                      htmlFor="createAccount"
                      className="ml-2 text-sm text-gray-600"
                    >
                      Üyelik oluştur.
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Butonlar */}
            <div className="mt-8 space-y-4">
              <button
                className="w-full bg-[#4F46E5] text-white py-4 rounded-lg hover:bg-[#4338CA] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleNext}
                disabled={isSubmitting || loading}
              >
                {isSubmitting ? "Sipariş Oluşturuluyor..." : "Devam Et"}
              </button>
              <button
                className="w-full bg-white text-gray-600 py-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                onClick={() => navigate(-1)}
              >
                Önceki
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddressDetails;
